import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Download, MapPin, Briefcase, GraduationCap, Award, Heart, Star, ChevronRight, ChevronLeft } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { members, TeamMember } from '../../data/members';
import SectionTitle from '../common/SectionTitle';
import AnimatedSection from '../ui/AnimatedSection';
import PlaceholderImage from '../ui/PlaceholderImage';
import SocialLinks from '../common/SocialLinks';
import Button from '../ui/Button';
import '../../styles/components/TeamMembers.css';

type ModalTab = 'about' | 'experience' | 'skills' | 'projects' | 'education';

const getInitials = (name: string): string => {
  if (name.startsWith('[')) return name.replace(/[[\]]/g, '').slice(0, 2).toUpperCase();
  return name.split(' ').map(w => w[0]).join('').slice(0, 3).toUpperCase();
};

interface MemberCardProps {
  member: TeamMember;
  index: number;
  onClick: () => void;
}

const MemberCard: React.FC<MemberCardProps> = ({ member, index, onClick }) => {
  const { t, language } = useLanguage();
  const name = language === 'vi' && member.nameVi ? member.nameVi : member.name;
  const role = language === 'vi' && member.roleVi ? member.roleVi : member.role;
  const bio = language === 'vi' && member.bioVi ? member.bioVi : member.bio;
  const isPlaceholder = member.isPlaceholder;

  const handleClick = () => {
    if (!isPlaceholder) onClick();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isPlaceholder && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <AnimatedSection animation="scale-in" delay={index * 150 + (isPlaceholder ? 100 : 0)} className="member-card__wrapper">
      <article
        className={`member-card${isPlaceholder ? ' member-card--placeholder' : ''}`}
        onClick={handleClick}
        role={isPlaceholder ? undefined : 'button'}
        tabIndex={isPlaceholder ? -1 : 0}
        aria-label={isPlaceholder ? undefined : `${t('team.viewProfile')}: ${name}`}
        onKeyDown={handleKeyDown}
      >
        {isPlaceholder && (
          <span className="member-card__badge">
            {language === 'vi' ? 'Sắp ra mắt' : 'Coming Soon'}
          </span>
        )}
        <div className="member-card__avatar">
          <PlaceholderImage
            id={member.avatar}
            label={`${name} avatar`}
            width={160}
            height={160}
            shape="circle"
            initials={isPlaceholder ? member.id.replace('member-', 'M') : getInitials(member.name)}
          />
        </div>
        <h3 className="member-card__name">{name}</h3>
        <p className="member-card__role">{role}</p>
        <p className="member-card__bio">{isPlaceholder ? bio : bio.slice(0, 100) + '...'}</p>
        {!isPlaceholder && (
          <span className="member-card__cta">
            {t('team.viewProfile')} <ChevronRight size={16} />
          </span>
        )}
      </article>
    </AnimatedSection>
  );
};

interface MemberModalProps {
  member: TeamMember;
  onClose: () => void;
}

const MemberModal: React.FC<MemberModalProps> = ({ member, onClose }) => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState<ModalTab>('about');
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const name = language === 'vi' && member.nameVi ? member.nameVi : member.name;
  const role = language === 'vi' && member.roleVi ? member.roleVi : member.role;
  const bio = language === 'vi' && member.bioVi ? member.bioVi : member.bio;
  const location = language === 'vi' && member.locationVi ? member.locationVi : member.location;

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    };
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key === 'Tab' && modalRef.current) {
      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const tabs: { id: ModalTab; label: string; icon: React.ReactNode }[] = [
    { id: 'about', label: t('team.modal.about'), icon: null },
    { id: 'experience', label: t('team.modal.experience'), icon: <Briefcase size={16} /> },
    { id: 'skills', label: t('team.modal.skills'), icon: null },
    { id: 'projects', label: t('team.modal.projects'), icon: null },
    { id: 'education', label: t('team.modal.education'), icon: <GraduationCap size={16} /> },
  ];

  const renderTabContent = () => {
    if (member.isPlaceholder) {
      return <p className="modal__placeholder-text">{bio}</p>;
    }

    switch (activeTab) {
      case 'about':
        return (
          <div className="modal__tab-content">
            <p className="modal__bio">{bio}</p>
            {member.softSkills && member.softSkills.length > 0 && (
              <div className="modal__subsection">
                <h4 className="modal__subsection-title">{t('team.modal.softSkills')}</h4>
                <ul className="modal__soft-skills">
                  {(language === 'vi' && member.softSkillsVi ? member.softSkillsVi : member.softSkills).map((skill, i) => (
                    <li key={i} className="modal__soft-skill">
                      <Star size={14} /> {skill}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {member.languages.length > 0 && (
              <div className="modal__subsection">
                <h4 className="modal__subsection-title">{t('team.modal.languages')}</h4>
                <div className="modal__languages">
                  {member.languages.map(lang => (
                    <div key={lang.name} className="modal__language">
                      <span className="modal__language-name">{lang.name}</span>
                      <span className="modal__language-level">
                        {language === 'vi' && lang.levelVi ? lang.levelVi : lang.level}
                      </span>
                      <p className="modal__language-desc">
                        {language === 'vi' && lang.descriptionVi ? lang.descriptionVi : lang.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {member.activities && member.activities.length > 0 && (
              <div className="modal__subsection">
                <h4 className="modal__subsection-title">{t('team.modal.activities')}</h4>
                <ul className="modal__activities">
                  {(language === 'vi' && member.activitiesVi ? member.activitiesVi : member.activities).map((act, i) => (
                    <li key={i} className="modal__activity">
                      <Heart size={14} /> {act}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      case 'experience':
        return (
          <div className="modal__tab-content">
            <div className="modal__timeline">
              {member.experience.map((exp, i) => (
                <div key={i} className="modal__timeline-item">
                  <div className="modal__timeline-dot" />
                  <div className="modal__timeline-content">
                    <div className="modal__timeline-header">
                      <h4>{exp.role} — {exp.company}</h4>
                      <span className="modal__timeline-period">{exp.period}</span>
                    </div>
                    <span className="modal__timeline-location">
                      <MapPin size={14} /> {exp.location}
                    </span>
                    {exp.projects.map((proj, j) => (
                      <div key={j} className="modal__exp-project">
                        <h5>{proj.name}</h5>
                        <p>{proj.description}</p>
                        <div className="modal__exp-meta">
                          <span>{t('team.modal.teamSize')}: {proj.teamSize}</span>
                        </div>
                        <div className="modal__tech-tags">
                          {proj.technologies.map(tech => (
                            <span key={tech} className="modal__tech-tag">{tech}</span>
                          ))}
                        </div>
                        <ul className="modal__responsibilities">
                          {proj.responsibilities.map((resp, k) => (
                            <li key={k}>{resp}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'skills':
        return (
          <div className="modal__tab-content">
            {member.skillCategories.map(cat => (
              <div key={cat.category} className="modal__skill-category">
                <h4>{language === 'vi' && cat.categoryVi ? cat.categoryVi : cat.category}</h4>
                <div className="modal__skill-tags">
                  {cat.items.map(skill => (
                    <span key={skill} className="modal__skill-tag">{skill}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      case 'projects':
        return (
          <div className="modal__tab-content">
            {member.experience.flatMap(exp => exp.projects).map((proj, i) => (
              <div key={i} className="modal__project-card">
                <h4>{proj.name}</h4>
                <p>{proj.description}</p>
                <div className="modal__tech-tags">
                  {proj.technologies.map(tech => (
                    <span key={tech} className="modal__tech-tag">{tech}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      case 'education':
        return (
          <div className="modal__tab-content">
            {member.education.length > 0 && (
              <div className="modal__subsection">
                {member.education.map((edu, i) => (
                  <div key={i} className="modal__edu-item">
                    <GraduationCap size={20} />
                    <div>
                      <h4>{edu.institution}</h4>
                      <p>{edu.degree}</p>
                      <span className="modal__edu-period">{edu.period}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {member.certificates.length > 0 && (
              <div className="modal__subsection">
                <h4 className="modal__subsection-title">{t('team.modal.certificates')}</h4>
                <div className="modal__certs">
                  {member.certificates.map((cert, i) => (
                    <div key={i} className="modal__cert">
                      <Award size={16} />
                      <span>{cert.name}</span>
                      <span className={`modal__cert-status modal__cert-status--${cert.status}`}>
                        {t(`team.modal.${cert.status}`)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="member-modal__backdrop" onClick={onClose} role="presentation">
      <div
        ref={modalRef}
        className="member-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`${name} - Profile`}
        onClick={e => e.stopPropagation()}
      >
        <button className="member-modal__close" onClick={onClose} aria-label="Close" autoFocus>
          <X size={24} />
        </button>

        <div className="member-modal__layout">
          {/* Left sidebar */}
          <aside className="member-modal__sidebar">
            <PlaceholderImage
              id={member.avatar}
              label={`${name} avatar`}
              width={180}
              height={180}
              shape="circle"
              initials={getInitials(member.name)}
              className="member-modal__avatar"
            />
            <h2 className="member-modal__name">{name}</h2>
            <p className="member-modal__role">{role}</p>
            <p className="member-modal__location">
              <MapPin size={14} /> {location}
            </p>
            <SocialLinks github={member.github} linkedin={member.linkedin} email={member.email} size={20} />
            <Button
              variant="outline"
              size="md"
              icon={<Download size={16} />}
              className="member-modal__download"
              ariaLabel={t('team.modal.downloadCv')}
            >
              {t('team.modal.downloadCv')}
            </Button>
          </aside>

          {/* Right content */}
          <div className="member-modal__content">
            {!member.isPlaceholder && (
              <div className="member-modal__tabs" role="tablist">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    className={`member-modal__tab ${activeTab === tab.id ? 'member-modal__tab--active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
            <div className="member-modal__tab-panel" role="tabpanel">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const useReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return reduced;
};

const useVisibleCount = () => {
  const [count, setCount] = useState(3);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setCount(w < 768 ? 1 : w < 1024 ? 2 : 3);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return count;
};

const TeamMembers: React.FC = () => {
  const { t } = useLanguage();
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<number>(0);
  const touchDeltaRef = useRef<number>(0);
  const trackWidthRef = useRef<number>(0);
  const visibleCount = useVisibleCount();
  const reducedMotion = useReducedMotion();

  const maxIndex = useMemo(() => Math.max(0, members.length - visibleCount), [visibleCount]);

  const canGoLeft = currentIndex > 0;
  const canGoRight = currentIndex < maxIndex;

  const slide = useCallback((direction: 'left' | 'right') => {
    if (isAnimating) return;
    const next = direction === 'right'
      ? Math.min(currentIndex + 1, maxIndex)
      : Math.max(currentIndex - 1, 0);
    if (next === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(next);
    setTimeout(() => setIsAnimating(false), 450);
  }, [currentIndex, maxIndex, isAnimating]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); slide('left'); }
    if (e.key === 'ArrowRight') { e.preventDefault(); slide('right'); }
  }, [slide]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = e.touches[0].clientX;
    touchDeltaRef.current = 0;
    trackWidthRef.current = carouselRef.current?.offsetWidth ?? 1;
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const delta = e.touches[0].clientX - touchStartRef.current;
    touchDeltaRef.current = delta;
    const pct = (delta / trackWidthRef.current) * 100;
    setDragOffset(pct);
  }, []);

  const handleTouchEnd = useCallback(() => {
    const delta = touchDeltaRef.current;
    setIsDragging(false);
    setDragOffset(0);
    if (Math.abs(delta) > 50) {
      slide(delta < 0 ? 'right' : 'left');
    }
    touchDeltaRef.current = 0;
  }, [slide]);

  const baseTranslateX = -(currentIndex * (100 / visibleCount));
  const totalTranslateX = baseTranslateX + (isDragging ? dragOffset : 0);

  return (
    <section id="team" className="section team-members">
      <div className="container">
        <SectionTitle title={t('team.title')} subtitle={t('team.subtitle')} />

        <div
          className="team-members__carousel"
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="region"
          aria-label="Team members carousel"
          aria-roledescription="carousel"
        >
          <button
            className={`carousel__btn carousel__btn--left${!canGoLeft ? ' carousel__btn--disabled' : ''}`}
            onClick={() => slide('left')}
            disabled={!canGoLeft || isAnimating}
            aria-label="Previous member"
          >
            <ChevronLeft size={24} />
          </button>

          <div className="carousel__viewport">
            <div
              ref={carouselRef}
              className={`carousel__track${isDragging ? ' carousel__track--dragging' : ''}`}
              style={{
                transform: `translateX(${totalTranslateX}%)`,
                transition: isDragging || reducedMotion ? 'none' : isAnimating ? 'transform 450ms cubic-bezier(0.25, 0.1, 0.25, 1)' : 'none',
              }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {members.map((member, i) => {
                const offset = i - currentIndex;
                const isVisible = offset >= 0 && offset < visibleCount;
                const isEdge = offset === -1 || offset === visibleCount;
                return (
                  <div
                    key={member.id}
                    className={`carousel__slide${isVisible ? ' carousel__slide--visible' : ''}${isEdge ? ' carousel__slide--edge' : ''}`}
                    style={{ flex: `0 0 ${100 / visibleCount}%` }}
                    aria-hidden={!isVisible}
                  >
                    <MemberCard
                      member={member}
                      index={i}
                      onClick={() => !member.isPlaceholder && setSelectedMember(member)}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <button
            className={`carousel__btn carousel__btn--right${!canGoRight ? ' carousel__btn--disabled' : ''}`}
            onClick={() => slide('right')}
            disabled={!canGoRight || isAnimating}
            aria-label="Next member"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        <div className="carousel__dots" role="tablist" aria-label="Carousel position">
          {Array.from({ length: maxIndex + 1 }, (_, i) => (
            <button
              key={i}
              className={`carousel__dot${i === currentIndex ? ' carousel__dot--active' : ''}`}
              onClick={() => { if (!isAnimating) { setIsAnimating(true); setCurrentIndex(i); setTimeout(() => setIsAnimating(false), 450); } }}
              role="tab"
              aria-selected={i === currentIndex}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {selectedMember && (
        <MemberModal member={selectedMember} onClose={() => setSelectedMember(null)} />
      )}
    </section>
  );
};

export default TeamMembers;
